import type { DashboardPersonaConfig } from '@components/pages/Settings/Analytics/Dashboard/dashboardSettings.constants'
import {
  DashboardRange,
  DashboardSection,
  DashboardTab,
} from '@constants/Dashboard/dashboard.enums'

/**
 * Section box titles per dashboard tab. This is the vocabulary used by the
 * live dashboard's hide/show editor (`hiddenSections` is keyed by tab → set of
 * these titles). It is shared here so the settings-driven widget → section
 * mapping below can be reconciled against a single source of truth.
 */
export const DASHBOARD_SECTIONS_BY_TAB: Record<string, DashboardSection[]> = {
  [DashboardTab.Overview]: [
    DashboardSection.Appointments,
    DashboardSection.TodaysSchedule,
    DashboardSection.Calendar,
    DashboardSection.PatientFlowTracker,
    DashboardSection.IncompleteTreatmentPlan,
    DashboardSection.OpenTreatmentPatients,
    DashboardSection.UtilizationMetrics,
    DashboardSection.Procedures,
    DashboardSection.ProviderUtilization,
    DashboardSection.ArAging,
    DashboardSection.Tasks,
    DashboardSection.Alerts,
    DashboardSection.Production,
    DashboardSection.Recall,
    DashboardSection.TodaysProgress,
    DashboardSection.Collection,
    DashboardSection.Recare,
    DashboardSection.Diagnosis,
    DashboardSection.TreatmentAcceptance,
    DashboardSection.ChairTime,
    DashboardSection.CaseAcceptance,
    DashboardSection.NoShowRate,
    DashboardSection.SameDayCancellation,
    DashboardSection.AppointmentOverrun,
    DashboardSection.RevenuePerVisit,
    DashboardSection.CaseAcceptanceRate,
    DashboardSection.AvgProviderUtilization,
    DashboardSection.TreatmentOpportunities,
    DashboardSection.ProductionTodays,
  ],
  [DashboardTab.Huddle]: [
    DashboardSection.TodaysForecast,
    DashboardSection.Patients,
    DashboardSection.ProviderPerformanceBreakdown,
    DashboardSection.YesterdaysPerformance,
    DashboardSection.Leaderboard,
    DashboardSection.Events,
    DashboardSection.OverdueActions,
    DashboardSection.YesterdaysActuals,
    DashboardSection.ScheduleAdjustments,
    DashboardSection.PerformanceVsGoal,
    DashboardSection.FollowUpsFromYesterday,
    DashboardSection.ProviderActualsYesterday,
    DashboardSection.TomorrowsForecast,
    DashboardSection.ScheduleAtAGlance,
    DashboardSection.PreparationForTomorrow,
    DashboardSection.GoalsForTomorrow,
  ],
  [DashboardTab.Operations]: [
    DashboardSection.RevenueOverview,
    DashboardSection.ScheduleOperations,
    DashboardSection.InsuranceClaims,
    DashboardSection.NetHygieneProduction,
    DashboardSection.ProductionPerHygieneVisit,
    DashboardSection.ArOver90Day,
    DashboardSection.PatientsAge,
    DashboardSection.PatientStatus,
    DashboardSection.ExpensesBreakdown,
    DashboardSection.SeatUtilization,
  ],
  [DashboardTab.Finance]: [
    DashboardSection.PracticeRevenueOverview,
    DashboardSection.Production,
    DashboardSection.CollectionPerformance,
    DashboardSection.InsuranceVsPatientCollection,
    DashboardSection.DentistVsHygienistProduction,
    DashboardSection.TreatmentValue,
    DashboardSection.ArAging,
    DashboardSection.OutstandingBalance,
    DashboardSection.Claims,
    DashboardSection.Collection,
    DashboardSection.CollectionRatio,
    DashboardSection.ProductionByDepartment,
    DashboardSection.NetHygieneProductionFinance,
    DashboardSection.ProductionPerHygieneVisitFinance,
    DashboardSection.ArOver90Day,
    DashboardSection.CollectionMetric,
    DashboardSection.CollectionRate,
    DashboardSection.ArAgingMetric,
    DashboardSection.ProductionByProcedure,
    DashboardSection.ProductionPerProvider,
  ],
  [DashboardTab.Performance]: [
    DashboardSection.SchedulingPerformance,
    DashboardSection.ProductionByProcedure,
    DashboardSection.UtilizationMetrics,
    DashboardSection.SeatUtilization,
    DashboardSection.ProviderUtilization,
    DashboardSection.ProductionPerProvider,
    DashboardSection.ProductionByDepartment,
    DashboardSection.DsoDaysToCollect,
    DashboardSection.Collection,
    DashboardSection.CollectionMetric,
    DashboardSection.CollectionRate,
    DashboardSection.ArAgingMetric,
    DashboardSection.ArOver90Day,
  ],
}

/**
 * Explicit mapping from a Settings → Dashboard widget id (see `DASHBOARD_WIDGETS`
 * in `dashboardSettings.constants.ts`) to the live-dashboard section title(s) it
 * controls. A section is hidden only when every widget mapped to it is off, so
 * one disabled widget cannot hide a card another widget still owns.
 *
 * The two vocabularies were designed independently, so this is a best-effort
 * reconciliation: only widgets with a clear section counterpart are mapped.
 * Widgets omitted here (or mapped to `[]`) currently have no live-dashboard
 * section to toggle — extend this table as sections/widgets are reconciled.
 */
export const WIDGET_ID_TO_SECTION_NAMES: Record<string, DashboardSection[]> = {
  // Financial
  'gross-production': [
    DashboardSection.Production,
    DashboardSection.ProductionTodays,
  ],
  'gross-collection': [
    DashboardSection.Collection,
    DashboardSection.CollectionPerformance,
  ],
  'net-collection': [
    DashboardSection.Collection,
    DashboardSection.CollectionPerformance,
  ],
  'insurance-patient-ar': [DashboardSection.InsuranceVsPatientCollection],
  'ar-aging': [
    DashboardSection.ArAging,
    DashboardSection.ArAgingMetric,
    DashboardSection.ArOver90Day,
  ],
  'production-per-provider': [DashboardSection.ProductionPerProvider],
  'production-per-visit': [
    DashboardSection.RevenuePerVisit,
    DashboardSection.ProductionPerHygieneVisit,
    DashboardSection.ProductionPerHygieneVisitFinance,
  ],
  'forecasted-revenue': [
    DashboardSection.TodaysForecast,
    DashboardSection.TomorrowsForecast,
  ],
  'scheduled-production': [
    DashboardSection.ScheduleOperations,
    DashboardSection.SchedulingPerformance,
  ],
  'revenue-growth': [
    DashboardSection.RevenueOverview,
    DashboardSection.PracticeRevenueOverview,
  ],
  // Operations
  'schedule-fill-rate': [
    DashboardSection.ScheduleOperations,
    DashboardSection.SchedulingPerformance,
  ],
  'open-chair-time': [DashboardSection.ChairTime],
  'provider-hygiene-utilization-rate': [
    DashboardSection.ProviderUtilization,
    DashboardSection.UtilizationMetrics,
    DashboardSection.AvgProviderUtilization,
  ],
  'no-show-rate': [DashboardSection.NoShowRate],
  'same-day-cancellation-rate': [DashboardSection.SameDayCancellation],
  'appointment-overrun-rate': [DashboardSection.AppointmentOverrun],
  'operatory-turnover-rate': [DashboardSection.SeatUtilization],
  'recall-rate': [DashboardSection.Recall, DashboardSection.Recare],
  // Performance
  'case-acceptance-rate': [
    DashboardSection.CaseAcceptance,
    DashboardSection.CaseAcceptanceRate,
  ],
  'large-case-acceptance-rate': [
    DashboardSection.CaseAcceptance,
    DashboardSection.CaseAcceptanceRate,
  ],
  'diagnosis-rate': [DashboardSection.Diagnosis],
}

/**
 * Sections tied to goal-progress display. Hidden when a persona's
 * `showGoalProgress` display preference is off. (Default is on, so this only
 * removes sections when a clinic explicitly opts out.)
 */
export const GOAL_PROGRESS_SECTION_NAMES: DashboardSection[] = [
  DashboardSection.TodaysProgress,
  DashboardSection.PerformanceVsGoal,
  DashboardSection.GoalsForTomorrow,
]

/**
 * Sections tied to alert banners. Hidden when a persona's `showAlertBanners`
 * display preference is off. NOTE: default is off, so alerts are hidden unless
 * a clinic opts in — this is a faithful application of the setting default and
 * should be confirmed with product (see plan §7 review note).
 */
export const ALERT_BANNER_SECTION_NAMES: DashboardSection[] = [
  DashboardSection.Alerts,
]

/**
 * Maps a persona's `date_range` display preference to the dashboard's period
 * range tab. `quarterly` has no dedicated tab, so it falls back to monthly.
 */
export const DATE_RANGE_TO_RANGE_KEY: Record<string, DashboardRange> = {
  daily: DashboardRange.OneDay,
  weekly: DashboardRange.OneWeek,
  monthly: DashboardRange.OneMonth,
  quarterly: DashboardRange.OneMonth,
  yearly: DashboardRange.OneYear,
}

/**
 * Widget-map keys used to persist layout hide/show for sections that have no
 * Settings → Dashboard widget counterpart (e.g. Huddle "Patients").
 */
export const LAYOUT_HIDDEN_WIDGET_PREFIX = 'layout-hidden:'

export function layoutHiddenWidgetId(tab: string, name: string): string {
  return `${LAYOUT_HIDDEN_WIDGET_PREFIX}${tab}:${name}`
}

export function isLayoutHiddenWidgetId(widgetId: string): boolean {
  return widgetId.startsWith(LAYOUT_HIDDEN_WIDGET_PREFIX)
}

function parseLayoutHiddenWidgetId(
  widgetId: string,
): { tab: string; name: string } | null {
  if (!isLayoutHiddenWidgetId(widgetId)) return null
  const rest = widgetId.slice(LAYOUT_HIDDEN_WIDGET_PREFIX.length)
  const colon = rest.indexOf(':')
  if (colon <= 0 || colon === rest.length - 1) return null
  return { tab: rest.slice(0, colon), name: rest.slice(colon + 1) }
}

function addHiddenSection(
  hidden: Record<string, Set<string>>,
  tab: string,
  name: string,
): void {
  const set = hidden[tab] ?? new Set<string>()
  set.add(name)
  hidden[tab] = set
}

function sectionListHas(names: readonly string[], name: string): boolean {
  return names.includes(name)
}

/** Widget ids that control a live section title. */
function widgetIdsForSection(name: string): string[] {
  const ids: string[] = []
  Object.entries(WIDGET_ID_TO_SECTION_NAMES).forEach(([widgetId, names]) => {
    if (sectionListHas(names, name)) ids.push(widgetId)
  })
  return ids
}

/**
 * Builds the `hiddenSections` map (tab → set of hidden section titles) for a
 * persona config by combining disabled widgets, layout hide/show flags, and
 * the goal-progress / alert-banner display preferences.
 */
export function buildHiddenSectionsFromConfig(
  config: DashboardPersonaConfig | undefined,
): Record<string, Set<string>> {
  const hidden: Record<string, Set<string>> = {}
  if (!config) return hidden

  const disabledSectionNames = new Set<string>()
  const widgets = config.widgets ?? {}

  Object.entries(widgets).forEach(([widgetId, enabled]) => {
    if (enabled) return
    const layoutHidden = parseLayoutHiddenWidgetId(widgetId)
    if (!layoutHidden) return
    addHiddenSection(hidden, layoutHidden.tab, layoutHidden.name)
  })

  const seenSectionNames = new Set<string>()
  Object.values(WIDGET_ID_TO_SECTION_NAMES).forEach((names) => {
    names.forEach((name) => {
      if (seenSectionNames.has(name)) return
      seenSectionNames.add(name)
      const ids = widgetIdsForSection(name)
      if (ids.length === 0) return
      const allDisabled = ids.every((id) => widgets[id] === false)
      if (allDisabled) disabledSectionNames.add(name)
    })
  })

  if (config.display?.showGoalProgress === false) {
    GOAL_PROGRESS_SECTION_NAMES.forEach((name) =>
      disabledSectionNames.add(name),
    )
  }

  if (config.display?.showAlertBanners === false) {
    ALERT_BANNER_SECTION_NAMES.forEach((name) => disabledSectionNames.add(name))
  }

  Object.entries(DASHBOARD_SECTIONS_BY_TAB).forEach(([tab, names]) => {
    names.forEach((name) => {
      if (disabledSectionNames.has(name)) addHiddenSection(hidden, tab, name)
    })
  })

  return hidden
}

/** Every section title known to the live dashboard, across all tabs. */
const ALL_KNOWN_SECTION_NAMES = new Set<string>(
  Object.values(DASHBOARD_SECTIONS_BY_TAB).flat(),
)

function tabsContainingSection(name: string): string[] {
  return Object.entries(DASHBOARD_SECTIONS_BY_TAB)
    .filter(([, names]) => sectionListHas(names, name))
    .map(([tab]) => tab)
}

/** True when `name` is hidden on every tab that actually lists that section. */
function isSectionHiddenOnAllTabs(
  hiddenSections: Record<string, Set<string>>,
  name: string,
): boolean {
  const tabs = tabsContainingSection(name)
  if (tabs.length === 0) {
    return Object.values(hiddenSections).some((set) => set.has(name))
  }
  return tabs.every((tab) => (hiddenSections[tab] ?? new Set()).has(name))
}

/**
 * Inverse of {@link buildHiddenSectionsFromConfig}: persist live hide/show
 * back onto the persona config. A widget is disabled only when every mapped
 * section is hidden on every tab that lists it. Widgets that share a section
 * keep their own on/off flags while that section stays visible. Remaining
 * per-tab hides use `layout-hidden:{tab}:{name}`.
 */
export function applyHiddenSectionsToConfig(
  config: DashboardPersonaConfig,
  hiddenSections: Record<string, Set<string>>,
): DashboardPersonaConfig {
  const allExistingHidden = (names: string[]): boolean => {
    const existing = names.filter((name) => ALL_KNOWN_SECTION_NAMES.has(name))
    return (
      existing.length > 0 &&
      existing.every((name) => isSectionHiddenOnAllTabs(hiddenSections, name))
    )
  }

  const widgets: Record<string, boolean> = {}
  Object.entries(config.widgets ?? {}).forEach(([widgetId, enabled]) => {
    if (!isLayoutHiddenWidgetId(widgetId)) widgets[widgetId] = enabled
  })
  Object.entries(WIDGET_ID_TO_SECTION_NAMES).forEach(([widgetId, names]) => {
    const existing = names.filter((name) => ALL_KNOWN_SECTION_NAMES.has(name))
    if (existing.length === 0) return
    const allHidden = existing.every((name) =>
      isSectionHiddenOnAllTabs(hiddenSections, name),
    )
    if (allHidden) {
      widgets[widgetId] = false
      return
    }

    const shared = existing.some((name) => widgetIdsForSection(name).length > 1)
    if (!shared) {
      widgets[widgetId] = true
      return
    }

    const siblingIds = new Set<string>()
    existing.forEach((name) => {
      widgetIdsForSection(name).forEach((id) => siblingIds.add(id))
    })
    let anyEnabled = false
    siblingIds.forEach((id) => {
      if (config.widgets?.[id] === true) anyEnabled = true
    })
    widgets[widgetId] = anyEnabled ? config.widgets?.[widgetId] === true : true
  })
  Object.entries(hiddenSections).forEach(([tab, set]) => {
    const known = new Set<string>(DASHBOARD_SECTIONS_BY_TAB[tab] ?? [])
    set.forEach((name) => {
      if (known.has(name)) widgets[layoutHiddenWidgetId(tab, name)] = false
    })
  })

  return {
    ...config,
    display: {
      ...config.display,
      showGoalProgress: !allExistingHidden(GOAL_PROGRESS_SECTION_NAMES),
      showAlertBanners: !allExistingHidden(ALERT_BANNER_SECTION_NAMES),
    },
    widgets,
  }
}
